'use client'

import OfferLinkArrow from './offer-link-arrow'
import OfferMessage from './offer-message'

import OfferUsd from './offer-usd'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { Bitcoin, CheckCircle2 } from 'lucide-react'
import { EXPLORER_URL } from '@/utils/constants'
import { offerFundingReplaced, OfferError, type Offer } from '@/utils/offers'
import { OFFER_POSTAGE_ACK_SATS } from '@/utils/offer-constants'
import { fetchLiveItemOutpoint } from '@/utils/offer-item'
import {
	acceptIncomingOffer,
	reconcileOffer,
	declineIncomingOffer,
	formatOfferBtc,
	offerLabel,
	offerSettlementPending,
	clearIdleSettlementMark,
	OfferOutcomeUnknown
} from '@/utils/offer-inbox'
import OfferDialog from './offer-dialog'
import { acceptStepStage, beginOfferFlow, type OfferTelemetrySurface } from '@/utils/offer-telemetry'
import OfferItemSummary from './offer-item-summary'
import OfferFloor, { useOfferFloorGap } from './offer-floor'
import { deepDiscountPercent } from '@/utils/offer-floor-gap'
import useInscription from '@/utils/hooks/useInscription'
import styles from './offer-dialog.module.css'

export default function ReviewOfferModal({
	offer,
	itemId,
	action = 'accept',
	onClose,
	onChanged,
	surface = 'offer_inbox'
}: {
	offer: Offer
	itemId: string
	action?: 'accept' | 'decline'
	onClose: () => void
	onChanged?: () => void
	/** Where the review was opened from, for failure telemetry. */
	surface?: OfferTelemetrySurface
}) {
	// A completed sale survives any remount: the receipt is kept for this tab.
	const receiptKey = `ow:sold-receipt:v1:${offer.id}`
	const savedReceipt = (() => {
		try {
			const raw = action === 'accept' ? sessionStorage.getItem(receiptKey) : null
			return raw ? (JSON.parse(raw) as { txid: string | null; pending: boolean }) : null
		} catch {
			return null
		}
	})()
	const [step, setStep] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(() =>
		action === 'accept' && !savedReceipt && offerSettlementPending(offer.id)
			? new OfferOutcomeUnknown().message
			: null
	)
	const [deliveryPending, setDeliveryPending] = useState(savedReceipt?.pending ?? false)
	const [settlementLost, setSettlementLost] = useState<false | 'refunded' | 'replaced'>(false)
	const [done, setDone] = useState(!!savedReceipt)
	const [uncertain, setUncertain] = useState(
		() => action === 'accept' && !savedReceipt && offerSettlementPending(offer.id)
	)
	const [txid, setTxid] = useState<string | null>(savedReceipt?.txid ?? null)
	const running = useRef(false)
	const { mutate } = useSWRConfig()
	const { data: saleItem } = useInscription(itemId)
	// An offer 30% or more under the floor asks once more before it sells:
	// the first tap on Accept names the discount, "Sell anyway" sells. It is a
	// second prompt, never a block.
	const floorSlug =
		action === 'accept' && !offer.nat_terms
			? offer.collection_slug || saleItem?.collection?.slug || null
			: null
	const { gap: floorGapInfo } = useOfferFloorGap(floorSlug, offer.price_sats)
	const deepDiscount = !!floorGapInfo?.deep
	const discountPercent = floorGapInfo ? deepDiscountPercent(floorGapInfo) : 0
	const [confirmingDiscount, setConfirmingDiscount] = useState(false)
	// Accepting sends the item's whole output to the buyer, not just the
	// inscription. Show the postage that leaves with it, read from the same
	// no-store lookup the acceptance itself uses, and above the
	// acknowledgement threshold ask the seller to confirm that exact amount.
	const {
		data: live,
		error: liveError,
		isValidating: liveChecking,
		mutate: reloadLive
	} = useSWR(
		action === 'accept' ? ['offers:item-output', itemId] : null,
		([, id]) => fetchLiveItemOutpoint(id),
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	)
	const postage = live?.sats ?? 0
	const postageKnown = !!live && !liveError
	const needsPostageAck = postageKnown && postage > OFFER_POSTAGE_ACK_SATS
	// The amount the seller ticked the box for; a new amount needs a new tick.
	const [acknowledgedSats, setAcknowledgedSats] = useState<number | null>(null)
	const postageAcknowledged = acknowledgedSats !== null && acknowledgedSats === postage
	const reviewing = action === 'accept' && !done && !uncertain && !settlementLost
	const acceptBlocked = reviewing && (!postageKnown || liveChecking || (needsPostageAck && !postageAcknowledged))
	const formatSats = (sats: number) => sats.toLocaleString('en-US')
	const run = useCallback(async () => {
		if (running.current || done || uncertain) return
		if (action === 'accept' && (!postageKnown || (needsPostageAck && !postageAcknowledged))) return
		running.current = true
		setError(null)
		setStep('Waiting for wallet…')
		const telemetry = beginOfferFlow(
			action === 'decline' ? 'offer_decline' : offer.scope === 'item' ? 'offer_accept' : 'offer_fill',
			surface,
			{
				scope: offer.scope,
				collectionSlug: offer.collection_slug,
				priceSats: offer.price_sats,
				offerId: offer.id,
				role: 'seller'
			}
		)
		telemetry.stage(action === 'decline' ? 'submit_decline' : 'check_offer')
		try {
			if (action === 'decline') await declineIncomingOffer(offer)
			else {
				const result = await acceptIncomingOffer(
					offer,
					itemId,
					(next) => {
						setStep(next)
						telemetry.stage(acceptStepStage(next))
					},
					{ reviewedPostage: postage }
				)
				setTxid(result.txid)
				setDeliveryPending(!!result.pending)
				try {
					sessionStorage.setItem(receiptKey, JSON.stringify({ txid: result.txid, pending: !!result.pending }))
				} catch {}
			}
			telemetry.success()
			setDone(true)
			void mutate((key) => Array.isArray(key) && String(key[0]).startsWith('offers:'))
			onChanged?.()
		} catch (e) {
			telemetry.failure(e)
			setUncertain(e instanceof OfferOutcomeUnknown)
			if (e instanceof OfferError && e.code === 'item_output_changed') {
				// Show the new amount and ask for a fresh acknowledgement.
				setAcknowledgedSats(null)
				void reloadLive()
			}
			setError(e instanceof Error ? e.message : 'The offer could not be updated.')
		} finally {
			running.current = false
			setStep(null)
		}
	}, [
		done,
		uncertain,
		action,
		offer,
		itemId,
		mutate,
		onChanged,
		surface,
		receiptKey,
		postage,
		postageKnown,
		needsPostageAck,
		postageAcknowledged,
		reloadLive
	])
	useEffect(() => {
		if (settlementLost || (!deliveryPending && !uncertain)) return
		let stopped = false
		const check = async () => {
			try {
				const current = await reconcileOffer(offer.id)
				if (!stopped && current.state === 'cancelled') {
					setSettlementLost(offerFundingReplaced(current) ? 'replaced' : 'refunded')
					setError(null)
					onChanged?.()
				}
				// An unknown outcome that the API shows as still open, with nothing
				// in flight, frees the seller to review the sale again.
				if (!stopped && uncertain && !deliveryPending && clearIdleSettlementMark(current)) {
					setUncertain(false)
					setError(null)
				}
				if (!stopped && current.state === 'accepted') {
					setDeliveryPending(false)
					setUncertain(false)
					setError(null)
					setDone(true)
					setTxid(current.accepted_txid)
					onChanged?.()
				}
			} catch {
				/* Preserve pending state on index/network errors. */
			}
		}
		void check()
		const timer = setInterval(check, 15000)
		return () => {
			stopped = true
			clearInterval(timer)
		}
	}, [offer.id, offer.nat_terms, deliveryPending, uncertain, settlementLost, onChanged])
	const declineStarted = useRef(false)
	useEffect(() => {
		if (action !== 'decline' || declineStarted.current) return
		declineStarted.current = true
		void run()
	}, [action, run])
	if (done && action === 'decline')
		return (
			<OfferDialog
				compact
				title="Offer declined"
				description="You successfully declined the offer."
				onClose={onClose}
				footer={
					<button className={styles.submit} type="button" onClick={onClose}>
						Done
					</button>
				}
			>
				<div className={styles.declineSuccess} role="status">
					<CheckCircle2 size={40} aria-hidden="true" />
					<span className="sr-only">Offer declined.</span>
				</div>
			</OfferDialog>
		)
	return (
		<OfferDialog
            headerDescription
			title={
				settlementLost
					? settlementLost === 'replaced'
						? 'Offer funding replaced'
						: 'Offer refunded'
					: done
					? action === 'accept'
						? 'Sold! 🎉'
						: 'Offer declined'
					: action === 'accept'
					? 'Accept Offer'
					: 'Decline Offer'
			}
			description={
				settlementLost
					? settlementLost === 'replaced'
						? 'The buyer’s funding was replaced before the sale could settle. This sale did not complete and you received no payment.'
						: 'The buyer’s refund confirmed first. This sale did not complete and you received no payment.'
					: done
					? action === 'accept'
						? `Congratulations — you sold into an offer for ${formatOfferBtc(offer.price_sats)} BTC. ${
								deliveryPending
									? offer.nat_terms
										? 'Waiting for confirmed, indexed NAT delivery.'
										: 'Your payout lands once Bitcoin confirms the sale.'
									: 'The sale confirmed on Bitcoin and your payout is in your wallet.'
						  }`
						: 'This offer has been declined.'
					: action === 'accept'
					? ''
					: 'Declining removes this offer from your waiting offers. Your item stays in your wallet.'
			}
			onClose={onClose}
			busy={!!step}
			footer={
				reviewing && deepDiscount && confirmingDiscount && !step ? (
					<div className={styles.discountConfirm} role="alertdialog" aria-labelledby="offer-discount-confirm">
						<p id="offer-discount-confirm" className={styles.discountConfirmText}>
							You are about to sell {discountPercent}% below the floor price. Are you sure?
						</p>
						<button type="button" className={styles.cancel} onClick={() => setConfirmingDiscount(false)}>
							Go back
						</button>
						<button
							type="button"
							className={`${styles.submit} ${styles.submitDanger}`}
							disabled={acceptBlocked}
							data-testid="offer-deep-discount-confirm"
							onClick={() => {
								setConfirmingDiscount(false)
								void run()
							}}
						>
							Sell anyway
						</button>
					</div>
				) : (
					<button
						className={styles.submit}
						type="button"
						disabled={!!step || acceptBlocked}
						onClick={
							done || uncertain || settlementLost
								? onClose
								: reviewing && deepDiscount
								? () => setConfirmingDiscount(true)
								: run
						}
					>

						{step ||
							(done || uncertain || settlementLost
								? 'Done'
								: action === 'accept'
								? 'Accept offer'
								: 'Decline offer')}
					</button>
				)
			}
		>
			<OfferItemSummary label={!done && action === 'accept' ? 'You sell' : undefined} showFloor={false} id={itemId} priceSats={offer.price_sats} onNavigate={onClose} hero={done && action === 'accept'}>
				{offer.scope !== 'collection' && <span>{offerLabel(offer)}</span>}
			</OfferItemSummary>
			{offer.nat_terms && (
				<div className="rounded-xl border border-border p-3 text-sm">
					<strong>You sell {offer.nat_terms.token_amount} NAT</strong>
					<p>≈ {offer.nat_terms.sats_per_nat} sats/NAT · One exact lot</p>
					<p className="text-xs text-muted-foreground">
						Fresh transfer only. Prior public sale signatures cannot be revoked by this acceptance.
					</p>
				</div>
			)}
			{!done && action === 'accept' && (floorSlug || saleItem?.collection?.slug) && <OfferFloor warningCard slug={floorSlug || saleItem.collection.slug} priceSats={offer.price_sats} />}
			{!settlementLost && (
				<div className={styles.proceeds}>
					<span>
						{action === 'decline' ? 'Offer amount' : done ? 'Sale proceeds' : 'You receive'}
					</span>
					<strong
						className={
							done && !deliveryPending && action === 'accept' ? styles.acceptedAmount : undefined
						}
					>
						<Bitcoin size={18} className={styles.bitcoin} aria-hidden="true" />
						{done && !deliveryPending && action === 'accept' ? '+' : ''}
						{formatOfferBtc(offer.price_sats)} BTC{' '}
						<OfferUsd sats={offer.price_sats} parentheses={done && action === 'accept'} />
					</strong>
				</div>
			)}
			{reviewing && (
				<div className={styles.itemOutput} data-testid="offer-item-output">
					{liveError ? (
						<p role="alert" className={styles.error}>
							{liveError instanceof Error ? liveError.message : 'The item output could not be checked.'}{' '}
							<button type="button" disabled={liveChecking} onClick={() => void reloadLive()}>
								Retry
							</button>
						</p>
					) : postageKnown ? (
						<p className={styles.hint}>
							Item output: {formatSats(postage)} sats go to the buyer with the inscription.
						</p>
					) : (
						<p role="status" className={styles.hint}>
							Checking the item output…
						</p>
					)}
					{needsPostageAck && (
						<label className={styles.radioRow}>
							<input
								type="checkbox"
								checked={postageAcknowledged}
								onChange={(e) => setAcknowledgedSats(e.target.checked ? postage : null)}
								data-testid="offer-postage-ack"
							/>
							<span className={styles.radioCopy}>
								<strong>
									I understand {formatSats(postage)} sats of BTC on this output transfer with the
									inscription
								</strong>
							</span>
						</label>
					)}
				</div>
			)}
			{!done && !settlementLost && <OfferMessage offer={offer} message={offer.message} />}

			{!done && !settlementLost && action === 'decline' && (
				<p className={styles.hint}>
					Your wallet may ask for a signature to confirm ownership. Declining does not transfer your item.
				</p>
			)}
			{done && !settlementLost && (
				<div role="status" className={styles.progress} data-testid="sale-receipt">
					<strong>{action === 'accept' ? (deliveryPending ? 'Sale submitted' : 'Sale confirmed') : 'Offer declined'}</strong>
					<p>{action === 'accept' && deliveryPending ? 'Confirmation is pending on Bitcoin. Your proceeds are on the way.' : action === 'accept' ? 'Your sale is confirmed on Bitcoin.' : 'This offer is no longer available to accept.'}</p>
					<div className={styles.receiptActions}>
						{txid && <a href={`${EXPLORER_URL}/tx/${txid}`} target="_blank" rel="noreferrer">View transaction <OfferLinkArrow /></a>}
						{action === 'accept' && <Link href="/wallet" onClick={onClose}>View in wallet <OfferLinkArrow /></Link>}
					</div>
				</div>
			)}
			{error && (
				<p role="alert" className={styles.error}>
					{error}
				</p>
			)}
		</OfferDialog>
	)
}
